import Button from "@/components/button"
import Card from "@/components/card"
import IncidentsSheet from "@/components/incidents-sheet"
import LinePicker, { LinePickerOption } from "@/components/line-picker"
import Topbar from "@/components/topbar"
import { useIPQCForm } from "@/hooks/ipqc"
import { Icon } from "@/icons/icon"
import RemarkDetailsSheet from "@/ipqc/components/remark-details-sheet"
import ResampleDetailsSheet from "@/ipqc/components/resample-details-sheet"
import { setFieldsToSubmitted } from "@/ipqc/lib/utils"
import { useWatch } from "@/lib/form/form"
import { FormContextProvider, useFormContext } from "@/lib/form/form-context"
import { useWidgetRegistry, WidgetRegistry } from "@/lib/form/hooks"
import DatePickerWidget from "@/lib/form/widgets/date-picker"
import DropdownWidget from "@/lib/form/widgets/dropdown"
import {
  FormSchema,
  InternalProcessSchema,
  MachineSchema as StageSchema,
  ParamSchema,
  ProductionLineSchema,
  ProductSchema,
} from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import { BreakdownSummary } from "@/lib/form/view"
import { cn, formatShiftTime } from "@/lib/utils"
import useStore from "@/store"
import { format } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router"

export function IpqcPreviousHistoryPage() {
  const params = useParams()
  const navigate = useNavigate()

  const getProductHistory = useStore.use.ipqc().getProductHistory
  const userStore = useStore.use.user()
  const { form, rebuildForm } = useIPQCForm({})
  const { history, formSchema } = useProductHistory()
  const registry = useWidgetRegistry({
    DATE: DatePickerWidget,
    DROPDOWN: DropdownWidget,
  })
  const lines = useLines()
  const [currLineId, setCurrLineId] = useState(lines.at(0)?.value || "")
  const currLineIdx = useMemo(() => {
    const index = history?.productionLines.findIndex(
      (line) => line.id === currLineId
    )

    return typeof index === "number" ? index : -1
  }, [currLineId, history])
  const currLine = useCurrLine()
  const submittedAt = useSubmittedDate()
  const shift = useFormattedShift()

  function useCurrLine() {
    const [curr, setCurr] = useState<ProductionLineSchema | null>(null)

    useEffect(() => {
      if (!history?.productionLines) {
        setCurr(null)

        return
      }

      if (currLineIdx >= 0) {
        setCurr(history.productionLines.at(currLineIdx) || null)

        return
      }
    }, [history, currLineIdx, currLineId])

    return curr
  }

  function useProductHistory() {
    const [formSchema, setFormSchema] = useState<FormSchema>({ products: [] })
    const [history, setHistory] = useState<ProductSchema | undefined>()

    useEffect(() => {
      if (params.id) {
        const newHistory = getProductHistory(params.id)
        setHistory(newHistory)

        if (newHistory) {
          const newFormSchema = {
            products: [newHistory],
          }
          setFormSchema(newFormSchema)
          rebuildForm({
            schema: newFormSchema,
            isHandover: false,
            defaultValues: newFormSchema,
          })
          setFieldsToSubmitted({
            form,
            schema: newFormSchema,
          })
        } else {
          setHistory(undefined)
          setFormSchema({
            products: [],
          })
          rebuildForm({
            schema: {
              products: [],
            },
            isHandover: false,
            defaultValues: {},
          })
        }
      }
    }, [params.id, form])

    return { history, formSchema }
  }

  function useLines() {
    const [lines, setLines] = useState<LinePickerOption[]>([])

    useEffect(() => {
      if (!history) {
        setLines([])

        return
      }

      setCurrLineId(history.productionLines.at(0)?.id || "")

      setLines(
        history.productionLines.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      )
    }, [history])

    return lines
  }

  function useSubmittedDate() {
    const [formatted, setFormatted] = useState("")

    useEffect(() => {
      if (!currLine?.filledAt) {
        setFormatted("")

        return
      }

      try {
        setFormatted(format(new Date(currLine.filledAt), "dd MMMM yy"))
      } catch {
        setFormatted("")
      }
    }, [currLine])

    return formatted
  }

  function useFormattedShift() {
    const [formatted, setFormatted] = useState("")

    useEffect(() => {
      if (
        !(
          currLine?.shiftName &&
          currLine.shiftStartTime &&
          currLine.shiftEndTime
        )
      ) {
        setFormatted("")

        return
      }

      setFormatted(
        `${currLine.shiftName}(${formatShiftTime(currLine.shiftStartTime)} - ${formatShiftTime(currLine.shiftEndTime)})`
      )
    }, [currLine])

    return formatted
  }

  async function handleClickBack() {
    await navigate("/ipqc/form")
  }

  return (
    <div className="h-screen overflow-auto">
      <Topbar
        className="justify-start sticky top-0 left-0 z-10"
        title="Last History Sheet"
        subtitle={userStore.plant.name}
        slotSide="left"
      >
        <Button className="mr-4" variant="ghost" onClick={handleClickBack}>
          <Icon name="ArrowRight" className="h-6 w-auto rotate-180" />
        </Button>
      </Topbar>

      {!!history && (
        <div className="mt-4 px-4">
          <h1 className="font-poppins text-lg mb-4">{history?.name}</h1>

          <FormContextProvider
            formSchema={formSchema}
            form={form}
            registry={registry}
          >
            <LinePicker
              options={lines}
              value={currLineId}
              onChange={setCurrLineId}
            />

            <div className="flex flex-col gap-4 mt-6">
              {currLine &&
                currLine.machines.map((stage, index) => (
                  <StageHistoryView
                    key={stage.id}
                    schema={stage}
                    registry={registry}
                    formSchema={formSchema}
                    path={`products[0].productionLines[${currLineIdx}].machines[${index}]`}
                  />
                ))}
            </div>

            <IncidentsSheet form={form} />
            <RemarkDetailsSheet form={form} />
            <ResampleDetailsSheet form={form} />
          </FormContextProvider>

          <div
            className="
              flex items-center gap-2 text-label-text text-sm
              mt-5 font-medium pb-8
            "
          >
            <div>Date: {submittedAt}</div>
            <div className="text-icon">•</div>
            <div>Shift: {shift}</div>
          </div>
        </div>
      )}
    </div>
  )
}

interface StageHistoryViewProps {
  registry: WidgetRegistry
  formSchema: FormSchema
  path?: string
  schema: StageSchema
}

function StageHistoryView({
  schema,
  path = "",
  ...props
}: StageHistoryViewProps) {
  const { form } = useFormContext()
  const breakdowns = useWatch({
    form,
    name: `${path}.breakdown`,
    defaultValue: [],
  })

  return (
    <Card className="px-0">
      <div className="flex justify-between items-center px-3">
        <h3 className="text-button-black font-poppins text-lg font-semibold">
          {schema.name}
        </h3>
      </div>

      <div className="flex flex-col gap-6 mt-4">
        {schema.internalProcesses.map((process, index) => (
          <div key={process.id}>
            {schema.internalProcesses.length > 1 && (
              <h4
                className="
                  uppercase px-4 py-2.5 font-semibold text-sm
                  bg-band-fill
                "
              >
                {process.name}
              </h4>
            )}

            <ProcessHistoryView
              className="mt-4 px-3"
              path={`${path}.internalProcesses[${index}]`}
              schema={process}
              {...props}
            />
          </div>
        ))}
      </div>

      <BreakdownSummary
        className="px-3"
        status={FormParamStatus.SUBMITTED}
        schema={schema}
        form={form}
        path={path}
        breakdowns={breakdowns}
      />
    </Card>
  )
}

interface ProcessHistoryViewProps {
  registry: WidgetRegistry
  formSchema: FormSchema
  path?: string
  className?: string
  schema: InternalProcessSchema
}

function ProcessHistoryView({
  className,
  schema,
  path = "",
  ...props
}: ProcessHistoryViewProps) {
  return (
    <section
      className={cn(
        `
          flex flex-col gap-4
        `,
        className
      )}
    >
      {schema.parameters.map((param, index) => (
        <ParamHistoryView
          key={param.id}
          path={`${path}.parameters[${index}]`}
          schema={param}
          {...props}
        />
      ))}
    </section>
  )
}

interface ParamHistoryViewProps {
  registry: WidgetRegistry
  formSchema: FormSchema
  path?: string
  className?: string
  schema: ParamSchema
}

function ParamHistoryView({
  schema,
  registry,
  path = "",
  formSchema,
}: ParamHistoryViewProps) {
  const Component = registry.get(schema.type)

  if (!Component) {
    return false
  }

  return <Component schema={schema} path={path} formSchema={formSchema} />
}
